import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useGetPreference, usePatchPreference } from "@/api/preference.api";
import { useGetUsers } from "@/api/user.api";
import { NotificationScopeEnum } from "@/constants/enums/notificationScope.enum";
import { NotificationScopeOptions } from "@/types/notificationScopeOptions";
import { StyledSelectMultiWoController } from "@/components/styled/StyledSelectMultiWoController";
import { GetPreferenceResponse } from "@/types/api/api-types";
import { StyledMainActionButton } from "@/components/styled/StyledMainActionButton";

export const Notifications = () => {
  const { t } = useTranslation(["boClients", "common"]);
  const [preferenceUpdate, setPreferenceUpdate] = useState<GetPreferenceResponse>();

  const { data: preference, isLoading, isError } = useGetPreference();
  const { mutate: patchPreference } = usePatchPreference();
  const { data: users } = useGetUsers();

  const handleScopeChange = (scope: NotificationScopeEnum, type: "pickup" | "expedition") => {
    if (!preferenceUpdate) return;
    if (type === "pickup") {
      setPreferenceUpdate({
        ...preferenceUpdate,
        notificationPickup: scope,
      });
    } else {
      setPreferenceUpdate({
        ...preferenceUpdate,
        notificationExpedition: scope,
      });
    }
  };

  const handleUpdatePreference = async () => {
    if (!preferenceUpdate) return;
    await patchPreference({
      expeditionAdmin: preferenceUpdate.expeditionAdmin,
      pickupAdmin: preferenceUpdate.pickupAdmin,
      notificationExpedition: preferenceUpdate.notificationExpedition,
      notificationPickup: preferenceUpdate.notificationPickup,
    });
  };

  useEffect(() => {
    setPreferenceUpdate(preference);
  }, [preference]);

  const handleMultiSelect = (
    value: { value: string; label: string }[],
    type: "pickup" | "expedition",
  ) => {
    if (!preferenceUpdate) return;
    if (type === "pickup") {
      setPreferenceUpdate({
        ...preferenceUpdate,
        pickupAdmin: value.map((el) => el.value),
      });
    } else {
      setPreferenceUpdate({
        ...preferenceUpdate,
        expeditionAdmin: value.map((el) => el.value),
      });
    }
  };

  const shouldDisplaySaveButton = JSON.stringify(preferenceUpdate) !== JSON.stringify(preference);

  if (isLoading) return <div>{t("common:states.loading")}</div>;
  if (isError || !preferenceUpdate) return <div>{t("profileNotifications.error")}</div>;

  return (
    <div className="flex flex-1 flex-col gap-8">
      <div className="flex flex-col gap-6 lg:px-8">
        <div className="flex flex-row justify-between gap-2 border-0 border-b border-gray-200 pb-5">
          <h2 className="text-lg font-semibold">{t("profileNotifications.title")}</h2>
          <div className={`${shouldDisplaySaveButton ? "" : "hidden"}`}>
            <StyledMainActionButton
              onClick={handleUpdatePreference}
              text={t("profileNotifications.save")}
              isLoading={false}
            />
          </div>
        </div>
        <div className="w-fulll flex flex-col justify-start gap-2">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700">
              {t("profileNotifications.pickupEmailLabel")}
            </span>
          </div>
          <div className="flex w-full flex-col gap-4 md:flex-row">
            <div className="flex w-fit flex-row rounded-lg border border-gray-200 bg-white p-0.5">
              {preferenceUpdate &&
                NotificationScopeOptions.map((scope) => (
                  <button
                    key={scope.value}
                    className={`flex w-fit flex-row items-center gap-2 rounded-lg px-2 py-2 focus:outline-hidden ${
                      scope.value === preferenceUpdate.notificationPickup
                        ? "bg-brand700 text-white"
                        : "bg-white"
                    }`}
                    onClick={() =>
                      handleScopeChange(scope.value as NotificationScopeEnum, "pickup")
                    }
                  >
                    <span className="text-sm font-medium">{scope.label}</span>
                  </button>
                ))}
            </div>
            {(preferenceUpdate.notificationPickup === "ALL" ||
              preferenceUpdate.notificationPickup === "ADMIN") && (
              <StyledSelectMultiWoController
                placeholder={t("profileNotifications.adminsPlaceholder")}
                isClearable={true}
                options={
                  users
                    ?.filter((user) => user.role === "ADMIN" || user.role === "SUPERADMIN")
                    .map((user) => ({
                      value: user.id,
                      label: user.firstName + " " + user.lastName,
                    })) || []
                }
                onChange={(e) => handleMultiSelect(e, "pickup")}
                className="h-fit w-full max-w-[400px] min-w-[200px]"
                value={preferenceUpdate.pickupAdmin.map((user) => ({
                  value: user,
                  label:
                    users?.find((el) => el.id === user)?.firstName +
                      " " +
                      users?.find((el) => el.id === user)?.lastName ||
                    t("profileNotifications.unknownUser"),
                }))}
              />
            )}
          </div>
        </div>
        <div className="w-fulll flex flex-col justify-start gap-2">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700">
              {t("profileNotifications.deliveryEmailLabel")}
            </span>
          </div>
          <div className="flex w-full flex-col gap-4 md:flex-row">
            <div className="flex w-fit flex-row rounded-lg border border-gray-200 bg-white p-0.5">
              {preferenceUpdate &&
                NotificationScopeOptions.map((scope) => (
                  <button
                    key={scope.value}
                    className={`flex w-fit flex-row items-center gap-2 rounded-lg px-2 py-2 focus:outline-hidden ${
                      scope.value === preferenceUpdate.notificationExpedition
                        ? "bg-brand700 text-white"
                        : "bg-white"
                    }`}
                    onClick={() =>
                      handleScopeChange(scope.value as NotificationScopeEnum, "expedition")
                    }
                  >
                    <span className="text-sm font-medium">{scope.label}</span>
                  </button>
                ))}
            </div>
            {(preferenceUpdate.notificationExpedition === "ALL" ||
              preferenceUpdate.notificationExpedition === "ADMIN") && (
              <StyledSelectMultiWoController
                placeholder={t("profileNotifications.adminsPlaceholder")}
                isClearable={true}
                options={
                  users
                    ?.filter((user) => user.role === "ADMIN" || user.role === "SUPERADMIN")
                    .map((user) => ({
                      value: user.id,
                      label: user.firstName + " " + user.lastName,
                    })) || []
                }
                onChange={(e) => handleMultiSelect(e, "expedition")}
                className="h-fit w-full max-w-[400px] min-w-[200px]"
                value={preferenceUpdate.expeditionAdmin.map((user) => ({
                  value: user,
                  label:
                    users?.find((el) => el.id === user)?.firstName +
                      " " +
                      users?.find((el) => el.id === user)?.lastName ||
                    t("profileNotifications.unknownUser"),
                }))}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
